/**
 * Which sections a client should actually see.
 *
 * The client sidebar listed twelve sections regardless of whether any of them
 * had anything in them. Measured against the real data, the best-served client
 * had content in six; the median was four; four clients had one or none. So a
 * client signed in, found eight empty rooms, and reasonably concluded there was
 * nothing here for them — which the sign-in numbers bear out: four of fourteen
 * had opened the portal in the last month, and four had never opened it at all.
 *
 * Two kinds of section, and the difference is whether emptiness is information.
 *
 * ALWAYS — the spine of the relationship. "No invoices yet" tells a client
 * something true and reassuring, and Messages must never be hidden because it
 * is how they reach you. These stay whatever the counts say.
 *
 * ON CONTENT — everything else. A Brand Kit with nothing in it is not a
 * feature, it is a room with no furniture. These appear when there is something
 * to see, and their appearing is itself a signal that something happened.
 *
 * Hiding a section never blocks it: the routes still resolve, so any link
 * already sent to a client keeps working.
 */

export const ALWAYS_VISIBLE = [
  '/dashboard', '/projects', '/files', '/messages', '/invoices',
  // Requests is where a client *asks* for something — it has a form, and the
  // client dashboard links to it. Emptiness there is the normal starting state,
  // not an absence, so hiding it would remove the way in rather than tidy away
  // a room. Same argument as Messages: never hide how somebody reaches you.
  '/requests',
]

/**
 * Section path -> the count that earns it a place.
 * The keys are what `sectionCounts` in the sidebar fills in.
 */
export const CONTENT_GATED = {
  '/onboarding': 'onboarding',
  // Everything below is something the studio sends *to* a client. They cannot
  // start any of it, so an empty one is genuinely nothing to look at.
  '/approvals': 'approvals',
  '/website-review': 'reviews',
  '/brand-assets': 'brandAssets',
  // The brand vault exists only once the studio maps a NAS folder to the client.
  '/vault': 'vault',
  '/proposals': 'proposals',
  '/meetings': 'meetings',
  '/forms': 'forms',
}

/**
 * Whether a section belongs in the nav.
 *
 * `counts` may be partial or still loading. An unknown count shows the section
 * rather than hiding it: flickering a section away once its query lands is
 * worse than showing one empty section for a moment, and a missing count is
 * not evidence of absence.
 */
export function isSectionVisible(path, counts = {}, { loaded = true } = {}) {
  if (ALWAYS_VISIBLE.includes(path)) return true
  const key = CONTENT_GATED[path]
  if (!key) return true
  if (!loaded) return true
  const n = counts[key]
  if (n === undefined || n === null) return true
  return n > 0
}

/** Filter a nav list. Items with no `path` (dividers and the like) pass through. */
export function visibleSections(items = [], counts = {}, opts = {}) {
  return items.filter(item => !item?.path || isSectionVisible(item.path, counts, opts))
}
